import { createContext, useContext, useReducer } from 'react'

const CartContext = createContext()

// Cart reducer to manage cart state
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(
        item => item.id === action.payload.id && 
                item.size === action.payload.size && 
                item.color === action.payload.color
      )
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id && 
            item.size === action.payload.size && 
            item.color === action.payload.color
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        }
      } else {
        return {
          ...state,
          items: [...state.items, action.payload]
        }
      }
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.cartId !== action.payload)
      }
    
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.cartId === action.payload.cartId
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      }
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      }
    
    case 'SET_SHIPPING_OPTION':
      return {
        ...state,
        shippingOption: action.payload
      }
    
    case 'SET_SHIPPING_ADDRESS':
      return {
        ...state,
        shippingAddress: action.payload
      }
    
    case 'SET_BILLING_ADDRESS':
      return {
        ...state,
        billingAddress: action.payload
      }
    
    default:
      return state
  }
}

// Initial cart state
const initialState = {
  items: [],
  shippingOption: 'pickup', // 'pickup' | 'delivery' | 'shipping'
  isOpen: false,
  shippingAddress: {
    fullName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: ''
  },
  billingAddress: {
    fullName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: ''
  }
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState)

  // Helper function to generate unique cart ID
  const generateCartId = (productId, size, color) => {
    return `${productId}-${size}-${color || 'default'}`
  }

  // Add item to cart
  const addToCart = (product, size, color = 'Black', quantity = 1) => {
    if (!size) {
      alert('Please select a size')
      return
    }

    const cartId = generateCartId(product.slug, size, color)
    
    const payload = {
      cartId,
      id: product.slug,
      title: product.title,
      price: parseFloat(product.price),
      size,
      color,
      quantity,
      image: product.primaryImage,
      category: product.category
    }
    
    dispatch({
      type: 'ADD_TO_CART',
      payload
    })
  }

  // Remove item from cart
  const removeFromCart = (cartId) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: cartId
    })
  }

  // Update item quantity
  const updateQuantity = (cartId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartId)
    } else {
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: { cartId, quantity }
      })
    }
  }

  // Clear entire cart
  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' })
  }

  // Set shipping option
  const setShippingOption = (option) => {
    dispatch({
      type: 'SET_SHIPPING_OPTION',
      payload: option
    })
  }

  // Set shipping address
  const setShippingAddress = (address) => {
    dispatch({
      type: 'SET_SHIPPING_ADDRESS',
      payload: address
    })
  }

  // Set billing address
  const setBillingAddress = (address) => {
    dispatch({
      type: 'SET_BILLING_ADDRESS',
      payload: address
    })
  }

  // Calculate cart totals
  const getCartTotals = () => {
    const subtotal = state.items.reduce((total, item) => total + (item.price * item.quantity), 0)
    let shipping = 0
    if (state.shippingOption === 'delivery') {
      shipping = 5.00 // local delivery
    } else if (state.shippingOption === 'shipping') {
      shipping = 8.00 // standard shipping
    }
    const tax = subtotal * 0.0825 // 8.25% tax rate (Texas)
    const total = subtotal + shipping + tax

    return {
      subtotal: subtotal.toFixed(2),
      shipping: shipping.toFixed(2),
      tax: tax.toFixed(2),
      total: total.toFixed(2)
    }
  }

  // Get total number of items in cart
  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0)
  }

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    setShippingOption,
    setShippingAddress,
    setBillingAddress,
    getCartTotals,
    getTotalItems
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
